/**
 * All the imports in this file are copied from the respective files.
 * Enable these imports back when the respective files are migrated to respective libraries
 * and the imports can be resolved.
**/

// import { AI_DRIVER_IDENTIFICATION_SETTINGS, CameraSettings } from '@app/admin/company/company.consts';
enum AI_DRIVER_IDENTIFICATION_SETTINGS {
  NONE = 'none',
  TELEMATICS = 'telematics',
  FACIAL_RECOGNITION = 'face_match',
  TELEMATICS_FACIAL = 'telematics_and_face_match',
}

enum CameraSettings {
  On = 'on',
  Off = 'off',
  OnIfEngineOn = 'on_if_engine_on',
  OffWhileIdling = 'off_while_idling',
  NoOverride = 'no_override',
}
// =======================================================================

// import { IFraudControlResponse } from '@app/admin/fleet-card/shared/fraud-control/fraud-control.interface';
interface IFraudControlResponse {
  text_auth_status: string;
}
// =======================================================================

// import { IServerTimecardJobType } from '@app/dashboard/workforce/timecards/timecards.interface';
type TIMECARD_JOB_TYPE_STATUS = 'active' | 'deactivated';
interface IServerTimecardJobType {
  id: number;
  name: string;
  status: TIMECARD_JOB_TYPE_STATUS;
  position: number | null;
}
// =======================================================================

// import { CARD_STATUS } from '@fleet-cards/main.constants';
enum CARD_STATUS {
  active = 'active',
  active_locked = 'active_locked',
  active_unlocked = 'active_unlocked',
  unassigned = 'unassigned',
  assigned = 'assigned',
  inactive = 'inactive',
  frozen = 'frozen',
  suspended = 'suspended',
  terminated = 'terminated',
}
// =======================================================================

export enum COMPANY_SEGMENT_TYPES {
  ENT = 'ENT',
  UPPER_SMB = 'UPPER SMB',
  MM = 'MM',
  SMB = 'SMB',
}

export enum COMPANY_SUB_SEGMENT_TYPES {
  ENT = 'ENT',
  VSB_SMB = 'VSB SMB',
  SSB_SMB = 'SSB SMB',
  MSB_SMB = 'MSB SMB',
  PREMIER_MM = 'Premier MM',
  EMERGING_MM = 'Emerging MM',
  STRATEGIC_ENT = 'Strategic ENT',
}

export enum FUEL_ECONOMY_UNIT {
  KM_PER_LITRE = 'kmpl',
  LITRE_PER_HUNDRED_KM = 'lp100km',
}

export enum USER_ACCOUNT_TYPE {
  TRIAL = 'Trial',
  ENTERPRISE = 'Enterprise',
  CUSTOMER = 'Customer',
}

// #region Use these after discussion with the international team
export enum UNIT_SYSTEMS {
  metric = 'metric',
  us_imperial = 'us_imperial',
  british_imperial = 'british_imperial',
}

export enum FUEL_ECONOMY_UNITS {
  kmpl = 'kmpl',
  mpguk = 'mpguk',
  mpgus = 'mpgus',
  lp100km = 'lp100km',
}

export enum DISTANCE_UNITS {
  km = 'km',
  mi = 'mi',
}

export enum SPEED_UNITS {
  kph = 'kph',
  mph = 'mph',
}

export enum FUEL_VOLUME_UNITS {
  l = 'l',
  gal = 'gal',
  gal_uk = 'gal_uk',
}

export enum TEMPERATURE_UNITS {
  c = 'c',
  f = 'f',
}

export enum WEIGHT_UNITS {
  t = 't',
  lb = 'lb',
  kg = 'kg',
}

export enum LENGTH_UNITS {
  m = 'm',
  ft = 'ft',
}

export enum VOLUME_UNITS {
  m3 = 'm3',
  ft3 = 'ft3',
}

export enum PRESSURE_UNITS {
  kpa = 'kpa',
  psi = 'psi',
}

export const UnitsPreferenceCombined = {
  ...FUEL_ECONOMY_UNITS,
  ...DISTANCE_UNITS,
  ...SPEED_UNITS,
  ...FUEL_VOLUME_UNITS,
  ...TEMPERATURE_UNITS,
  ...WEIGHT_UNITS,
  ...LENGTH_UNITS,
  ...VOLUME_UNITS
} as const; // `as const` ensures type safety

export type UnitsPreferences = typeof UnitsPreferenceCombined[keyof typeof UnitsPreferenceCombined];

export const ALL_UNITS_SHORT: Record<string, string> = {
  [FUEL_ECONOMY_UNITS.kmpl]: $localize`kmpl`,
  [FUEL_ECONOMY_UNITS.mpguk]: $localize`mpguk`,
  [FUEL_ECONOMY_UNITS.mpgus]: $localize`mpgus`,
  [FUEL_ECONOMY_UNITS.lp100km]: $localize`lp100km`,

  [DISTANCE_UNITS.km]: $localize`km`,
  [DISTANCE_UNITS.mi]: $localize`mi`,

  [SPEED_UNITS.kph]: $localize`kph`,
  [SPEED_UNITS.mph]: $localize`mph`,

  [FUEL_VOLUME_UNITS.l]: $localize`L`,
  [FUEL_VOLUME_UNITS.gal]: $localize`gal`,
  [FUEL_VOLUME_UNITS.gal_uk]: $localize`gal (UK)`,

  [TEMPERATURE_UNITS.c]: $localize`c`,
  [TEMPERATURE_UNITS.f]: $localize`f`,

  [WEIGHT_UNITS.t]: $localize`t`,
  [WEIGHT_UNITS.lb]: $localize`lb`,
  [WEIGHT_UNITS.kg]: $localize`kg`,

  [LENGTH_UNITS.m]: $localize`m`,
  [LENGTH_UNITS.ft]: $localize`ft`,

  [VOLUME_UNITS.m3]: $localize`m3`,
  [VOLUME_UNITS.ft3]: $localize`ft3`,
};

export const ALL_UNITS_SINGULAR: Record<string, string> = {
  [FUEL_VOLUME_UNITS.gal]: $localize`gallon`,
  [FUEL_VOLUME_UNITS.l]: $localize`litre`,
  [FUEL_VOLUME_UNITS.gal_uk]: $localize`UK gallon`,

  [DISTANCE_UNITS.km]: $localize`kilometer`,
  [DISTANCE_UNITS.mi]: $localize`mile`,

  [SPEED_UNITS.kph]: $localize`kilometer per hour`,
  [SPEED_UNITS.mph]: $localize`mile per hour`,

  [WEIGHT_UNITS.lb]: $localize`pound`,
  [WEIGHT_UNITS.kg]: $localize`kilogram`,
  [WEIGHT_UNITS.t]: $localize`tonne`,

  [LENGTH_UNITS.ft]: $localize`foot`,
  [LENGTH_UNITS.m]: $localize`metre`,
};

export const ALL_UNITS_PLURAL: Record<string, string> = {
  [FUEL_VOLUME_UNITS.gal]: $localize`gallons`,
  [FUEL_VOLUME_UNITS.l]: $localize`litres`,
  [FUEL_VOLUME_UNITS.gal_uk]: $localize`UK gallons`,

  [DISTANCE_UNITS.km]: $localize`kilometers`,
  [DISTANCE_UNITS.mi]: $localize`miles`,

  [SPEED_UNITS.kph]: $localize`kilometers per hour`,
  [SPEED_UNITS.mph]: $localize`miles per hour`,

  [WEIGHT_UNITS.lb]: $localize`pounds`,
  [WEIGHT_UNITS.kg]: $localize`kilograms`,
  [WEIGHT_UNITS.t]: $localize`tonnes`,

  [LENGTH_UNITS.ft]: $localize`feet`,
  [LENGTH_UNITS.m]: $localize`metres`,
};

export enum UNIT_FORMATS {
  short = 'short', // l, gal, km
  plural = 'plural', // litres, gallons, kilometers
  singular = 'singular', // litre, gallon, kilometer
}

export const ALL_UNITS_TEXTS: Record<UNIT_FORMATS, Record<string, string>> = {
  short: ALL_UNITS_SHORT,
  plural: ALL_UNITS_PLURAL,
  singular: ALL_UNITS_SINGULAR,
};
// #endregion Use this after discussion with the international team

export type UnitSystem = 'metric' | 'us_imperial' | 'british_imperial';

export interface IResponsePreferredLocale {
  display_name: string;
  locale: string;
  preferred_time_format: string | null;
  preferred_number_format: string | null;
  onboard_locale_status?: string | null;
}
export type FuelEconomyPreference = 'mpguk' | 'mpgus' | 'kmpl' | 'lp100km';
export type DistancePreference = 'km' | 'mi';
export type SpeedPreference = 'kph' | 'mph';
export type FuelVolumePreference = 'gal' | 'l' | 'gal_uk';
export type TemperaturePreference = 'c' | 'f';
export type WeightPreference = 'lb' | 'kg' | 't';
export type LengthPreference = 'ft' | 'm';
export type VolumePreference = 'ft3' | 'm3';
export type PressurePreference = typeof PRESSURE_UNITS[keyof typeof PRESSURE_UNITS];

export interface IPreferredUnits {
  fuel_economy: FuelEconomyPreference;
  distance: DistancePreference;
  speed: SpeedPreference;
  fuel_volume: FuelVolumePreference;
  temperature: TemperaturePreference;
  weight: WeightPreference;
  length: LengthPreference;
  volume: VolumePreference;
  pressure?: PressurePreference;
}

export interface IResponseUser {
  associated_user: boolean,
  company_connection: IResponseCompanyConnection;
  company_role: string;
  connected_user?: boolean;
  created_at: string;
  cycle: string;
  dashboard_settings: string;
  dashboard_upsell_status: any;
  email: string;
  first_name: string;
  fleet_card_status: CARD_STATUS;
  groups: IResponseNestedGroup[];
  id: number;
  last_name: string;
  last_signature_url: string;
  language: string;
  metric_units: boolean;
  phone: string;
  primary_user?: IResponsePrimaryUser;
  recommended_app_notification_hidden: boolean;
  role: string;
  survey_data: any;
  time_zone: string;
  eld_mode: string;
  fuel_economy_unit: string;
  ignore_drive_time_app_version: number;
  dvir_version?: string;
  is_partner_user: boolean;
  phone_country_code?: string;
  phone_country_iso?: string;
  compliance_driving_events_backfilled_date?: string;
  preferred_unit_system?: UnitSystem;
  preferred_units?: IPreferredUnits;
  // TODO remove IResponsePreferredLocale once BE changes are merged
  preferred_locale?: string | IResponsePreferredLocale;
  preferred_time_format?: string | null;
  preferred_number_format?: string | null;
  onboard_locale_status?: string | null;
  currency?: string;
}

export interface IResponsePrimaryUser {
  email: string;
  first_name: string;
  id: number;
  last_name: string;
  phone: string;
  role: string;
}

export interface IResponseCompanyConnection {
  company: IResponseCompany;
  company_connection_user_roles?: IResponseCompanyConnectRoles[];
  company_id: number;
  enabled_features: string[];
  group_visibility: 'all' | 'limited' | 'none';
  id: number;
  role: string;
  user_id: number;
  permissions: {[key: string]: number[]};
}

export type CompanyProperty = keyof IResponseCompany;

export interface IResponseCompany {
  account_executive: IResponseUserAccount;
  account_manager: IResponseUserAccount;
  allow_drivers_to_edit_settings: boolean;
  annotation_tag_enabled: boolean;
  api_enabled: boolean;
  assigned_assets: number;
  assigned_elds: number;
  auto_annotate_udes_in_geofences_enabled: boolean;
  automated_coaching_enabled: boolean;
  bendix_enabled: boolean;
  billing_eligible: boolean;
  shop_eligible: boolean;
  support_eligible: boolean;
  ecomm_eligibility: string;
  canada_cycle_switching_requirement_enabled: boolean;
  canadian_mandate_output_files_enabled: boolean;
  city: string;
  claimed_driving_period_indicator_enabled: boolean;
  company_id: string;
  compliance_mode: string;
  composite_fleet_size: number;
  country?: string;
  cycle: string;
  dashboard_upsell_pro_trial_end_date: string;
  dashboard_upsell_pro_trial_start_date: string;
  dashboard_locked: boolean;
  data_retention_enabled: boolean;
  disable_safety_scores: boolean;
  driver_license_requirement_enabled: boolean;
  drivers_with_exception_wait_time: boolean;
  earn_cash_tab_disabled: boolean;
  eld_enabled_drivers: number;
  enabled_features: string[];
  enforce_reason_on_log_edits: boolean;
  exception_8_hour_break: boolean;
  exception_24_hour_restart: boolean;
  exception_ca_farm_school_bus: boolean;
  exception_short_haul: boolean;
  exception_wait_time: boolean;
  exclude_from_aggregate_data: boolean;
  exempt_logs_assignment_enabled: boolean;
  facility_insights_enabled: boolean;
  freight_carried: string[];
  fleet_card_application_status: string;
  onboarding_experience?: string;
  dispatch_pricing_experiment_status: string;
  has_tachograph_company_cards: boolean;
  has_tachograph_files: boolean;
  hos_sorting_enabled: boolean;
  fv_polling_time_enabled: boolean;
  id: number;
  incab_audio_recording: CameraSettings;
  driver_match: CameraSettings;
  road_image_breadcrumb: CameraSettings;
  image_check_enabled: boolean;
  industry: string;
  log_audit_enabled: boolean;
  log_edits_history_enabled: boolean;
  is_long_idling_settings_configured: boolean;
  long_idling_threshold_mins: number;
  maintenance_reminders_enabled: boolean;
  metric_units: boolean;
  minute_logs: boolean;
  min_alert_overage_kph: number;
  min_alert_threshold_time: number; // time unit is 'second'
  name: string;
  freight: string[];
  new_hos_rules_start_date: string;
  num_asset_gateways: number;
  num_cell_eld_devices: number;
  operating_eld_modes: string;
  pc_limit_jurisdiction: string;
  pc_limit_km_threshold: number;
  posted_speed_duration_threshold: number;
  posted_speed_overage_limit: number;
  realtime_main_map_enabled: boolean;
  recertify_logs_enabled: boolean;
  recommended_app_count: number;
  reverse_geocoding_enabled: boolean;
  safety_overview_enabled: boolean;
  smart_thresholds_available: boolean;
  smart_thresholds_enabled: boolean;
  driver_coaching_enabled: boolean;
  stop_sign_violation_default_high_severity: boolean;
  drive_for_embedded_webview_enabled: boolean;
  same_day_edits_v2_enabled: boolean;
  sds_reclassification_enabled: boolean;
  set_eld_engine_off_checkin_mode_on: boolean;
  sf_account_status: string;
  sf_trial_status?: boolean;
  short_haul_enabled: boolean;
  show_split_sleeper_toggle: boolean;
  slb_access: string;
  slb_mobile_driver_app_feature_flags: string;
  slb_opt_out: boolean;
  slb_web_fleet_dashboard_feature_flags: string;
  speeding_enabled: boolean;
  sso_enabled: boolean;
  login_preferences: IResponseLoginPreferences;
  state: string;
  street: string;
  subscription_plan: 'Starter' | 'Pro' | 'Enterprise' | 'ELD Starter' | 'ELD Plus';
  compliance_ai_driver_suggestion: AI_DRIVER_IDENTIFICATION_SETTINGS;
  compliance_ai_driver_assignment: AI_DRIVER_IDENTIFICATION_SETTINGS;
  time_zone: string;
  tpms_enabled: boolean;
  timecard_job_types: IServerTimecardJobType[];
  trackandtrace_formatted_address_enabled: boolean;
  unassigned_assets: number;
  unassigned_elds: number;
  undo_claim_enabled: boolean;
  unidentified_vehicle_movement_alert_enabled: boolean;
  video_recall_enabled: boolean;
  wifi_hotspot_data_threshold: number;
  z_cc_expiration_month: number;
  z_cc_expiration_year: number;
  z_account_number: number;
  exempt_driving_configuration_enabled: boolean;
  zip: string;
  fuel_insights_enabled: boolean;
  driver_hub_enabled: boolean;
  segment_type: COMPANY_SEGMENT_TYPES;
  segment_type_sf: COMPANY_SEGMENT_TYPES;
  sf_account_sub_segment: COMPANY_SUB_SEGMENT_TYPES;
  custom_dvir: boolean;
  aux_enabled: boolean;
  ifta_enabled: boolean;
  contains_pto_vehicles: boolean;
  sort_drivers_by_hos: boolean;
  idle_telematics_data: boolean;
  asset_custom_type_enabled: boolean;
  dvir_version: string;
  kt_driver_workflow_enabled: boolean;
  dvir_third_signature_enabled: boolean;
  allow_unregulated_vehicle_profiles: boolean;
  enable_fuel_theft_detection: boolean;
  show_driver_vehicle_location_history_report: boolean;
  hide_deactivated_driver_profile?: boolean;
  vehicle_availability_simplified_version_enabled?: boolean;
  hide_na_compliance_enabled?: boolean;
  has_timecards_drivers?: boolean;
  settings: IResponseSetting;
  safety_ai_recap_enabled: boolean;
  auto_share_coaching_summary: boolean;
  auto_mark_positive_events_deserves_recognition?: boolean;
  alert_driving_enabled?: boolean;
  safe_distancing_enabled?: boolean;
  event_capture_enabled_with_driver_privacy: boolean;
  partner_event_dispute_email: string;
  partner_name: string;
  partner_support_email: string;
  carb_ctc_test_enabled?: boolean | null;
  parts_and_inventory_enabled?: boolean;
  vmrs_enabled?: boolean;
  currency?: string;
  compliance_driving_events_population_start_datetime: string;
  allow_driver_creation_without_contact_info?: boolean;
  docs_attachment_limits?: IDocsAttachmentLimits;
}

export interface IDocsAttachmentLimits {
  max_attachments_per_component: number;
  max_attachment_component_count: number;
}

export interface IResponseCompanyConnectRoles {
  company_connection_user_role: {
    id: number;
    group_ids: number[];
    user_role: IResponseUserRoles;
  }
}

export interface IResponseUserRoles {
  id: number;
  name: string;
  status: string;
  permissions: string[];
}

export interface IResponseUserAccount {
  email: string;
  id: number;
  name: string;
  phone: string;
  photo_url: string;
}

export interface IResponseLoginPreferences {
  admin: string;
  driver: string;
  fleet_user: string;
}

export interface IResponseNestedGroup {
  group: IResponseGroup;
}

export interface IResponseGroup {
  id: number;
  name: string;
}

export interface IResponseSetting {
  fraud_controls: IFraudControlResponse
}

export interface IPartialUserInfo {
  userId: number | null;
  userEmail: string | null;
  userRole: string | null;
  compositeFleetSize: number | null;
  companyId: number | null;
}

export interface ICIAMToken {
  sub?: number;
  exp?: number;
  iat?: number;
  email?: string;
  roles: string[];
  fleet_company_id?: number;
  [key: string]: any;
}
